import { ElementController, getData, FORM_EVENT, nextPaint } from 'js-common';

import { TABLE_CONTROLLER_ID } from './constants';

const SORT_VALUE_DATA_ATTR = 'sort-value';

const ROW_COLORED_CLASS = 'row-colored';

// Filter value for when no particular term is selected. Keep in sync with HTML.
const SELECT_LIST_DEFAULT_FILTER_TERM = 'all';

/**
 * Table controller with sorting (ascending or descending) and filtering.
 */
export default class Table extends ElementController {
  static id = TABLE_CONTROLLER_ID;

  activeFilters = {
    gender: [],
    age: [],
  };

  /**
   * DOM loaded, bind events.
   */
  init() {
    // Sort table
    this.tableHead = this.selectSingle('thead');
    this.on(this.tableHead, 'click', 'button', this.handleSort);

    // Filter table
    // IMPORTANT! Never cache the table rows or any of its elements in a
    // variable. The rows are replaced with page updates and the variable will
    // point to a node no longer in the DOM.
    this.tableFilterGender = this.selectSingle('gender');
    this.tableFilterAge = this.selectSingle('age');
    this.noResults = this.selectSingle('no-results');
    this.clearFilterButton = this.selectSingle('filter-clear-btn');
    this.filterButtons = this.selectAll('filter-button');
    this.visibleRowCount = this.selectSingle('visible-row-count');

    // Select list for filtering
    this.selectListCountry = this.selectSingle('select-list-country');
    this.selectListYear = this.selectSingle('select-list-year');

    // Check if select list is used in the filter template
    this.hasSelectListFilter = !!(
      this.selectListCountry && this.selectListYear
    );

    // Check if filter button is used in the filter template
    this.hasFilterButtons = !!(this.tableFilterGender && this.tableFilterAge);

    // Apply filter at first page load.
    this.applyFilters();

    if (this.hasFilterButtons) {
      this.on(
        this.tableFilterGender,
        'click',
        'button',
        this.handleGenderFilter,
      );
      this.on(this.tableFilterAge, 'click', 'button', this.handleAgeFilter);
    }
    if (this.hasSelectListFilter) {
      this.on(this.selectListCountry, 'change', this.handleSelectFilter);
      this.on(this.selectListYear, 'change', this.handleSelectFilter);
    }
    if (this.hasFilterButtons || this.hasSelectListFilter) {
      this.on(this.clearFilterButton, 'click', this.clearFilter);
    }
    this.on(document.body, FORM_EVENT.response, this.handleRowColor);
  }

  /**
   * Handle sort for tables.
   *
   * @param {Event} e - Click event.
   */
  handleSort = (e) => {
    const selectedTableHead = e.currentTarget.closest('th');

    const currentSortValue = selectedTableHead.getAttribute('aria-sort');

    const newSortValue =
      currentSortValue === 'descending' || currentSortValue === null
        ? 'ascending'
        : 'descending';

    const sortType = getData(e.currentTarget, 'sort-type');
    const tableData = [...selectedTableHead.parentNode.children];
    this.currentIndex = tableData.indexOf(e.currentTarget.closest('th'));

    const tableBodyRows = this.selectAll(
      this.selectSingle('tbody'),
      'horse-table-row',
    );

    const data = [];

    tableBodyRows.forEach((row) => {
      const horseInfo = row.nextElementSibling;
      data.push({ tr: row, trInfo: horseInfo, cells: row.children });
    });

    const sorted =
      sortType === 'numeric'
        ? this.sortNumeric(data, newSortValue)
        : this.sortAlphabetic(data, newSortValue);

    [...selectedTableHead.parentNode.children].forEach((th) => {
      th.removeAttribute('aria-sort');
    });
    selectedTableHead.setAttribute('aria-sort', newSortValue);

    const newTbody = document.createElement('tbody');

    sorted.forEach((row) => {
      newTbody.append(row.tr);
      newTbody.append(row.trInfo);
    });

    const oldTbody = this.selectSingle('tbody');
    oldTbody.replaceWith(newTbody);
    this.handleRowColor();
  };

  /**
   * Sort numeric type.
   *
   * @param {Array} data - Table data, array with objects.
   * @param {string} sortValue - Sort value, ascending or descending.
   * @returns {Array} - Sorted table data
   */
  sortNumeric(data, sortValue) {
    return data.sort((a, b) => {
      const itemA = getData(a.cells[this.currentIndex], SORT_VALUE_DATA_ATTR);
      const itemB = getData(b.cells[this.currentIndex], SORT_VALUE_DATA_ATTR);

      if (sortValue === 'descending') {
        return (itemB != null) - (itemA != null) || itemA - itemB;
      }
      return (itemB != null) - (itemA != null) || itemB - itemA;
    });
  }

  /**
   * Sort alphabetic type.
   *
   * @param {Array} data - Table data, array with objects.
   * @param {string} sortValue - Sort value, ascending or descending.
   * @returns {Array} - Sorted table data
   */
  sortAlphabetic(data, sortValue) {
    data.sort((a, b) => {
      const itemA = getData(a.cells[this.currentIndex], SORT_VALUE_DATA_ATTR);
      const itemB = getData(b.cells[this.currentIndex], SORT_VALUE_DATA_ATTR);

      // equal items sort equally
      if (itemA === itemB) {
        return 0;
      }
      if (!itemA) {
        return 1;
      }
      if (!itemB) {
        return -1;
      }
      if (sortValue === 'ascending') {
        return itemA.toLowerCase().localeCompare(itemB.toLowerCase());
      }
      return itemB.toLowerCase().localeCompare(itemA.toLowerCase());
    });

    return data;
  }

  /**
   * Clear filtering
   */
  clearFilter = () => {
    Object.keys(this.activeFilters).forEach((filter) => {
      this.activeFilters[filter] = [];
    });

    this.filterButtons.forEach((btn) => {
      btn.classList.remove('filter-item-active');
    });
    if (this.hasSelectListFilter) {
      this.resetSelectElements();
    }

    this.applyFilters();

    this.handleRowColor();
    this.handleVisibleRowsCount();
    this.clearFilterButton.disabled = true;
  };

  /**
   * Add the clicked age filter.
   *
   * @param {Event} e - Click event.
   */
  handleAgeFilter = (e) => {
    this.handleTypeFilter('age', e.currentTarget);
  };

  /**
   * Add the clicked gender filter.
   *
   * @param {Event} e - Click event.
   */
  handleGenderFilter = (e) => {
    this.handleTypeFilter('gender', e.currentTarget);
  };

  /**
   * Handle table row color when filtering. Add color to every second row.
   */
  handleRowColor = async () => {
    await nextPaint();

    const rows = this.selectAll('horse-table-row');
    const visibleRows = rows.filter((row) => !row.hasAttribute('hidden'));
    for (let i = 0; i < visibleRows.length; i += 1) {
      if (i % 2 === 0) {
        visibleRows[i].classList.add(ROW_COLORED_CLASS);
      } else {
        visibleRows[i].classList.remove(ROW_COLORED_CLASS);
      }
    }
  };

  /**
   * Handle table row color when filtering. Add color to every second row.
   */
  handleVisibleRowsCount() {
    const rows = this.selectAll('horse-table-row');
    const visibleRows = rows.filter((row) => !row.hasAttribute('hidden'));
    if (!this.visibleRowCount) return;
    this.visibleRowCount.textContent = visibleRows.length;
  }

  /**
   * Handle filtering for tables.
   *
   * @param {string} type - Filter type.
   * @param {Element} filterButton - Button that added the filter.
   */
  handleTypeFilter = (type, filterButton) => {
    this.clearFilterButton.disabled = false;
    const filterValue = getData(filterButton, 'filter');

    let currentFilters = this.activeFilters[type];
    if (currentFilters.includes(filterValue)) {
      filterButton.classList.remove('filter-item-active');
      currentFilters = currentFilters.filter((item) => item !== filterValue);
    } else {
      filterButton.classList.add('filter-item-active');
      currentFilters = currentFilters.concat(filterValue);
    }

    // Add to state
    this.activeFilters[type] = currentFilters;

    this.applyFilters();
  };

  /**
   * Determine if the row should be visibile depending on selected values of
   * filter buttons.
   *
   * @param {HTMLElement} tableRow - A table row
   * @returns {boolean} - if row is visible
   */
  isVisibleByFilterButtons = (tableRow) => {
    const filterKeys = Object.keys(this.activeFilters);
    return filterKeys.every((key) => {
      const selectedFiltersByType = this.activeFilters[key];

      if (!selectedFiltersByType.length) {
        return true;
      }

      // Check if `4+`filter button is selected.
      let filterNum = '';
      selectedFiltersByType.forEach((selectedFilter) => {
        if (/^\d+\+$/.test(selectedFilter)) {
          filterNum = parseInt(selectedFilter, 10);
        }
      });

      const HorseRowValue = getData(tableRow, `${key}-filter`);

      if (filterNum) {
        return (
          HorseRowValue >= filterNum ||
          selectedFiltersByType.includes(HorseRowValue)
        );
      }
      return selectedFiltersByType.includes(HorseRowValue);
    });
  };

  /**
   * Determine if the row should be visibile depending on selected values of
   * select lists.
   *
   * @param {HTMLElement} tableRow - A table row
   * @returns {boolean} - if row is visible
   */
  isVisibleBySelectList = (tableRow) => {
    const filterTermCountry = this.selectListCountry.value;
    const filterTermYear = this.selectListYear.value;

    const countryData = getData(tableRow, 'country-filter');
    const yearData = getData(tableRow, 'year-filter').toString();

    return !!(
      (countryData === filterTermCountry ||
        filterTermCountry === SELECT_LIST_DEFAULT_FILTER_TERM) &&
      yearData === filterTermYear
    );
  };

  /**
   * Handle the changed select list filter.
   */
  handleSelectFilter = () => {
    this.clearFilterButton.disabled = false;
    this.applyFilters();
  };

  /**
   * Show or hide rows depending on selected filters.
   * Display text if result is empty.
   */
  applyFilters = () => {
    let hasResults = false;
    const filterItems = this.selectAll('filter-item');

    if (!filterItems.length) return;
    filterItems.forEach((horseRow) => {
      const isVisibleByFilterButtons = this.isVisibleByFilterButtons(horseRow);

      // Always set to true if there are no select lists in filter
      const isVisibleBySelectList = this.hasSelectListFilter
        ? this.isVisibleBySelectList(horseRow)
        : true;

      if (isVisibleByFilterButtons && isVisibleBySelectList) {
        hasResults = true;
        horseRow.removeAttribute('hidden');
      } else {
        horseRow.setAttribute('hidden', '');
      }
    });

    this.handleRowColor();
    this.handleVisibleRowsCount();

    // Show or hide the 'no results' text
    if (hasResults) {
      this.noResults.setAttribute('hidden', '');
    } else {
      this.noResults.removeAttribute('hidden');
    }
  };

  /** Reset select list to default values (first option). */
  resetSelectElements() {
    this.selectListCountry.selectedIndex = 0;
    this.selectListYear.selectedIndex = 0;
  }
}
